import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { getDoc, getDocFromServer, setDoc } from 'firebase/firestore';
import * as Sentry from '@sentry/angular';
import { AuthService } from '@auth';
import { IReplicableEntity } from '@shared/models/replicable-entity';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { convertToDate } from '@shared/helpers/convert-to-date';
import { QueryStringHelper } from 'src/app/shared/helpers/query-string.helper';
import { AppSettingsService } from '../app-settings.service';
import { FirestoreService } from '../firestore.service';
import { SystemSettingsService } from '../system-settings.service';
import { EntityConfig } from './entity-config';
import { EntityRegistry } from './entity-registry.service';

/**
 * Restores replicated data from the Desktop app back into Firestore (and
 * therefore IndexedDB) when the browser loses local data.
 *
 * Conflict rule: `Synced` always beats non-`Synced`; among equal statuses the
 * higher `updatedDate` wins; on a tie the Desktop version is preferred.
 */
@Injectable({ providedIn: 'root' })
export class RecoveryService {
  private readonly http = inject(HttpClient);
  private readonly firestoreService = inject(FirestoreService);
  private readonly authService = inject(AuthService);
  private readonly entityRegistry = inject(EntityRegistry);
  private readonly systemSettingsService = inject(SystemSettingsService);

  private readonly nativeServiceBaseUrl: string;
  private isRestoring = false;

  constructor() {
    this.nativeServiceBaseUrl = this.normalizeBaseUrl(
      AppSettingsService.appSettings.edaraNativeService.baseUrl
    );
  }

  /**
   * Restore all registered replicable entity types. Safe to call repeatedly
   * and concurrently — overlapping invocations are coalesced.
   */
  async restoreAllReplicatedData(): Promise<void> {
    if (this.isRestoring) {
      console.log('[RecoveryService] Restore already in progress, skipping.');
      return;
    }
    if (!this.authService.isAuthenticated?.()) return;

    this.isRestoring = true;
    try {
      if (!(await this.systemSettingsService.isDataReplicationEnabled())) return;

      const isFirestoreLoggedIn = await this.firestoreService.isLoggedIn();
      if (!isFirestoreLoggedIn) {
        await this.authService.renewFirebaseToken();
      }

      for (const config of this.entityRegistry.getAllConfigs()) {
        if (!config.replicateWhenOffline) continue;
        try {
          await this.restoreEntityType(config);
        } catch (error) {
          console.error(`[RecoveryService] Failed to restore ${config.entityType}:`, error);
          Sentry.captureException(error);
        }
      }
    } finally {
      this.isRestoring = false;
    }
  }

  private async restoreEntityType(config: EntityConfig): Promise<void> {
    const tenantId = this.authService.currentTenant;

    // `unsynced` is special-cased by the Desktop generic Find as
    // `syncStatus != 'Synced'`, so this single query returns UnSynced,
    // Syncing and Failed records.
    const [notSynced, synced] = await Promise.all([
      this.findOnDesktop(config.entityType, tenantId, 'unsynced'),
      this.findOnDesktop(config.entityType, tenantId, ReplicationSyncStatus.Synced)
    ]);

    // The two Desktop queries never overlap (`unsynced` is `!= Synced`), so the
    // origin of each record is known and the `Synced` scope can be applied to
    // exactly the records it is meant for.
    const candidates = [
      ...notSynced.map(raw => ({ raw, fromSyncedQuery: false })),
      ...synced.map(raw => ({ raw, fromSyncedQuery: true }))
    ];
    if (candidates.length === 0) return;

    // Optional per-entity scopes, resolved once per run: one for already-`Synced`
    // records (sales orders restore only the currently open closure) and one
    // for the rest (Order 3.0 restores only the current cashier's own). A scope
    // that cannot be resolved restores NOTHING of its kind — the entities that
    // define one never restored those records before the scope existed, so
    // flooding Firestore with the whole history is the one wrong answer.
    const syncedRestoreFilter = await this.resolveRestoreFilter(config, 'Synced', config.createSyncedRestoreFilter);
    const unsyncedRestoreFilter = await this.resolveRestoreFilter(config, 'unsynced', config.createUnsyncedRestoreFilter);

    console.log(
      `[RecoveryService] ${config.entityType}: evaluating ${candidates.length} Desktop record(s) for restore...`
    );

    for (const { raw, fromSyncedQuery } of candidates) {
      const desktopEntity = this.stripDesktopFields(raw);
      const localId = desktopEntity.metadata?.localId;
      if (!localId) continue;
      if (desktopEntity.metadata?.organizationInfo?.id !== tenantId) continue;
      const restoreFilter = fromSyncedQuery ? syncedRestoreFilter : unsyncedRestoreFilter;
      if (restoreFilter && !restoreFilter(desktopEntity)) continue;

      const existing = await this.getFromFirestore<IReplicableEntity>(
        config.firestoreCollection,
        localId
      );

      if (existing && (config.restoreOnlyIfMissing || !this.desktopWins(desktopEntity, existing))) continue;

      this.normalizeForRestore(desktopEntity);
      if (config.saveRestoredEntity) {
        await config.saveRestoredEntity(desktopEntity);
      } else {
        await this.saveToFirestore(config.firestoreCollection, localId, desktopEntity);
      }
    }
  }

  /**
   * A restore scope for one kind of Desktop record, or `undefined` when the
   * entity defines none (everything of that kind is restored). A scope whose
   * resolution fails restores none of that kind.
   */
  private async resolveRestoreFilter(
    config: EntityConfig,
    kind: 'Synced' | 'unsynced',
    factory?: () => Promise<(entity: IReplicableEntity) => boolean>
  ): Promise<((entity: IReplicableEntity) => boolean) | undefined> {
    if (!factory) return undefined;
    return factory().catch(error => {
      console.warn(
        `[RecoveryService] ${config.entityType}: could not resolve the ${kind} restore scope; restoring none.`,
        error
      );
      return () => false;
    });
  }

  /**
   * Conflict rule: `Synced` beats non-`Synced`; among equal statuses the
   * higher `updatedDate` wins; on a tie the Desktop version is preferred.
   */
  private desktopWins(desktop: IReplicableEntity, local: IReplicableEntity): boolean {
    const desktopSynced = this.isSynced(desktop);
    const localSynced = this.isSynced(local);
    if (desktopSynced !== localSynced) return desktopSynced;

    const desktopAt = this.lastChangedAt(desktop);
    const localAt = this.lastChangedAt(local);
    if (desktopAt !== localAt) return desktopAt > localAt;

    return true; // tie → Desktop preferred
  }

  private isSynced(entity: IReplicableEntity): boolean {
    return entity.metadata?.syncInfo?.syncStatus === ReplicationSyncStatus.Synced;
  }

  private lastChangedAt(entity: IReplicableEntity): number {
    const stamp = entity.metadata?.updatedDate ?? entity.metadata?.createdDate;
    if (!stamp) return 0;
    // Firestore returns date fields as Timestamp objects, the Desktop returns
    // ISO strings — convertToDate normalizes both before comparison.
    const date = convertToDate(stamp as any);
    const time = date instanceof Date ? date.getTime() : NaN;
    return isNaN(time) ? 0 : time;
  }

  /**
   * A restored record is the local source of truth again. Any non-`Synced`
   * status (including a stranded `Syncing`) is normalized to `UnSynced` so
   * the backend-sync pipeline picks it up; `Synced` is preserved.
   */
  private normalizeForRestore(entity: IReplicableEntity): void {
    entity.metadata = entity.metadata ?? ({} as any);
    if (!this.isSynced(entity)) {
      entity.metadata!.syncInfo = {
        ...(entity.metadata!.syncInfo ?? {}),
        syncStatus: ReplicationSyncStatus.UnSynced
      };
    }
    entity.metadata!.replicationInfo = {
      ...(entity.metadata!.replicationInfo ?? ({} as any)),
      restoreDate: new Date()
    };
  }

  private async findOnDesktop(
    entityType: string,
    tenantId: string,
    syncStatus: string
  ): Promise<any[]> {
    const queryString = new QueryStringHelper().toQueryString({
      TenantId: tenantId,
      syncStatus
    });
    return firstValueFrom(
      this.http.get<any[]>(
        `${this.nativeServiceBaseUrl}/generic/${entityType}/Find?${queryString}`
      )
    ).catch(() => [] as any[]);
  }

  private stripDesktopFields(raw: any): IReplicableEntity {
    const { _id, _rev, type, replicationTimestamp, ...entity } = raw ?? {};
    return entity as IReplicableEntity;
  }

  private async getFromFirestore<T>(
    collection: string,
    localId: string
  ): Promise<T | undefined> {
    const ref = this.firestoreService.doc(`${collection}/${localId}`);
    try {
      // Authoritative SERVER read. Recovery runs right after reconnect/login,
      // when a default getDoc() can still be served from the local cache. A
      // stale `fromCache` result previously made an existing local record
      // look "missing" and let the Desktop copy overwrite it. We read from
      // the server so the diff is against the true Firestore state.
      const snap = await getDocFromServer(ref);
      return snap.exists() ? (snap.data() as T) : undefined;
    } catch {
      // Server unreachable (offline). The Desktop `Find` also needs the
      // network, so there are normally no candidates to diff in this case;
      // fall back to the cached snapshot rather than failing the diff.
      try {
        const cached = await getDoc(ref);
        return cached.exists() ? (cached.data() as T) : undefined;
      } catch {
        return undefined;
      }
    }
  }

  private async saveToFirestore(
    collection: string,
    localId: string,
    entity: IReplicableEntity
  ): Promise<void> {
    const ref = this.firestoreService.doc(`${collection}/${localId}`);
    await setDoc(ref, JSON.parse(JSON.stringify(entity)));
  }

  private normalizeBaseUrl(url: string): string {
    return url?.endsWith('/') ? url.slice(0, -1) : url;
  }
}
